import { useMemo } from 'react';
import { AlertTriangle, ArrowUpCircle, ArrowDownCircle, UserX } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PartnerMemberAnalyticsRow } from '@/hooks/useEnterprisePartnerAnalytics';

/**
 * M3 (#573): cross-workspace per-member adoption table.
 *
 * Mirrors `AnalyticsLeaderboard`'s markup (plain `<table>` inside a `<Card>`,
 * built-in own loading/error/empty states) but is fed by the partner-scoped
 * `partner_member_analytics` rollup. Columns: User / Workspaces / Calls /
 * Meaningful % / New Contacts / New Connections.
 *
 * - `meaningful_pct === null` (zero calls) renders "—" — distinct from "0%"
 *   (had calls, none meaningful).
 * - Seat-waste rows get a badge + muted styling.
 * - Top / bottom adopter (by total activity) are highlighted from the returned
 *   set (no extra DB column).
 */

interface MemberAnalyticsTableProps {
  members: PartnerMemberAnalyticsRow[];
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
}

function adoptionScore(m: PartnerMemberAnalyticsRow): number {
  return m.calls + m.contacts_created + m.connections_created;
}

export function MemberAnalyticsTable({
  members,
  isLoading,
  error,
  onRetry,
}: MemberAnalyticsTableProps): JSX.Element {
  // Top/bottom adopter ids, computed from the returned set. Only meaningful when
  // there are ≥2 members with any activity (otherwise highlighting is noise).
  const { topId, bottomId } = useMemo(() => {
    const withActivity = members.filter((m) => adoptionScore(m) > 0);
    if (withActivity.length < 2) return { topId: null, bottomId: null };
    const sorted = [...withActivity].sort(
      (a, b) => adoptionScore(b) - adoptionScore(a),
    );
    return {
      topId: sorted[0].user_id,
      bottomId: sorted[sorted.length - 1].user_id,
    };
  }, [members]);

  if (isLoading) {
    return (
      <Card className="p-4">
        <h2 className="mb-3 text-sm font-medium">Members</h2>
        <Skeleton className="h-48 w-full" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4">
        <h2 className="mb-3 text-sm font-medium">Members</h2>
        <div className="flex h-48 flex-col items-center justify-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-muted-foreground">Couldn&rsquo;t load members</p>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h2 className="mb-3 text-sm font-medium">Members</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">User</th>
              <th className="py-2 pr-4 font-medium">Workspaces</th>
              <th className="py-2 pr-4 font-medium tabular-nums">Calls</th>
              <th className="py-2 pr-4 font-medium tabular-nums">Meaningful %</th>
              <th className="py-2 pr-4 font-medium tabular-nums">New Contacts</th>
              <th className="py-2 font-medium tabular-nums">New Connections</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isTop = m.user_id === topId;
              const isBottom = m.user_id === bottomId;
              return (
                <tr
                  key={m.user_id}
                  className={cn(
                    'border-b last:border-0',
                    m.seat_waste && 'bg-muted/40 text-muted-foreground',
                  )}
                >
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{m.email}</span>
                      {m.seat_waste && (
                        <Badge variant="outline" className="gap-1 text-amber-600">
                          <UserX className="h-3 w-3" />
                          Unused seat
                        </Badge>
                      )}
                      {isTop && (
                        <Badge
                          variant="secondary"
                          className="gap-1 bg-green-100 text-green-700"
                        >
                          <ArrowUpCircle className="h-3 w-3" />
                          Top adopter
                        </Badge>
                      )}
                      {isBottom && !isTop && (
                        <Badge variant="outline" className="gap-1 text-muted-foreground">
                          <ArrowDownCircle className="h-3 w-3" />
                          Lowest adopter
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {m.workspaces.length > 0 ? m.workspaces.join(', ') : '—'}
                  </td>
                  <td className="py-2 pr-4 tabular-nums">{m.calls}</td>
                  <td className="py-2 pr-4 tabular-nums">
                    {m.meaningful_pct === null
                      ? '—'
                      : `${m.meaningful_pct.toFixed(1)}%`}
                  </td>
                  <td className="py-2 pr-4 tabular-nums">{m.contacts_created}</td>
                  <td className="py-2 tabular-nums">{m.connections_created}</td>
                </tr>
              );
            })}
            {members.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-muted-foreground">
                  No members yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
